import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import UlcmContactService from 'src/services/UlcmContact.service';
import OtpCodeInput from 'src/components/shared/OtpCodeInput';
import OtpResendBlock from 'src/components/shared/OtpResendBlock';
import { useOtpResend } from 'src/hooks/useOtpResend';
import { formatPhoneNumber as formatPhoneDisplay } from 'src/utils/formatNumber';
import type { UlcmContactsPayload } from 'src/services/UlcmContact.service';
import type { ResendResponse } from 'src/types/otpResend';
import { cardSx } from './accountHubStyles';
import {
  extractApiError,
  isAccountHubEventForUser,
  isFullContactSnapshot,
} from './accountHubUtils';
import ContactSection, { type ContactSectionHandle } from './ContactSection';
import type {
  ContactRowData,
  DeleteTarget,
  PendingProposal,
} from './contactInfo';
import { proxyLoginWarning } from './contactInfo';

// ---------------------------------------------------------------------------
// Pending proposal resend control
// ---------------------------------------------------------------------------

interface PendingProposalResendProps {
  proposal: {
    otp_id: string;
    contact_type: string;
    contact_value: string;
    replace_id?: number | null;
  };
  asUser: string;
}

const PendingProposalResend: FC<PendingProposalResendProps> = ({
  proposal,
  asUser,
}) => {
  const sendFn = useCallback(async (): Promise<ResendResponse> => {
    const result =
      proposal.contact_type === 'email'
        ? await UlcmContactService.proposeEmail(
            proposal.contact_value,
            undefined,
            asUser,
            proposal.replace_id ?? undefined
          )
        : await UlcmContactService.proposePhone(
            proposal.contact_value,
            undefined,
            asUser,
            proposal.replace_id ?? undefined
          );
    // The propose endpoints return the resend-ladder metadata alongside the
    // OTP fields; normalize to ResendResponse (defaults match useOtpResend).
    return {
      next_allowed_at: result.next_allowed_at ?? null,
      cooldown_seconds_remaining: result.cooldown_seconds_remaining ?? 0,
      resend_count: result.resend_count ?? 0,
      resend_count_max: result.resend_count_max ?? 3,
      support_contact: result.support_contact ?? null,
      exhausted: result.exhausted ?? false,
    };
  }, [
    proposal.contact_type,
    proposal.contact_value,
    asUser,
    proposal.replace_id,
  ]);

  const resend = useOtpResend({ sendFn });
  return <OtpResendBlock resend={resend} />;
};

// ---------------------------------------------------------------------------
// AccountContactsEditor
//
// Self-contained email + phone editor backed by the ULCM contacts API.
// Owns fetching, pending-proposal confirmation, removal, and live WebSocket
// reconciliation. Used by the Account Hub Contact Info page (self or proxied)
// and by the NRE household member edit screen (proxy-only, gated by elevated
// authenticator auth).
// ---------------------------------------------------------------------------

export interface AccountContactsEditorProps {
  /** Query-string suffix for API calls: '' for self, '?as_user_id=<id>' when proxying. */
  asUser: string;
  /** User id whose contacts are being edited / whose WS events should apply. */
  targetUserId: number | null;
  /**
   * First name of the contact owner when editing another person's contacts.
   * Drives proxy-aware copy ("Alex's account" instead of "your account") and
   * the login-username warning. Omit for self-service.
   */
  ownerName?: string;
  /** Called whenever the contacts snapshot changes, e.g. for preference state. */
  onContactsChange?: (contacts: UlcmContactsPayload | null) => void;
}

const AccountContactsEditor: FC<AccountContactsEditorProps> = ({
  asUser,
  targetUserId,
  ownerName,
  onContactsChange,
}) => {
  // Keep a stable ref to the current targetUserId so the WS handler closure can
  // read it without being re-created on every render.
  const targetUserIdRef = useRef<number | null>(targetUserId);
  useEffect(() => {
    targetUserIdRef.current = targetUserId;
  }, [targetUserId]);

  const [contacts, setContacts] = useState<UlcmContactsPayload | null>(null);
  const [loading, setLoading] = useState(true);

  // Pending proposal confirm/cancel
  const [confirmingOtpId, setConfirmingOtpId] = useState<string | null>(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmSaving, setConfirmSaving] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Imperative handles to drive each section's glow/fade from the OTP confirm flow.
  const emailSectionRef = useRef<ContactSectionHandle>(null);
  const phoneSectionRef = useRef<ContactSectionHandle>(null);

  const onContactsChangeRef = useRef(onContactsChange);
  useEffect(() => {
    onContactsChangeRef.current = onContactsChange;
  });

  useEffect(() => {
    onContactsChangeRef.current?.(contacts);
  }, [contacts]);

  const fetchContacts = useCallback(async (): Promise<void> => {
    try {
      const data = await UlcmContactService.listContacts(asUser);
      setContacts(data);
    } catch {
      setContacts(null);
    } finally {
      setLoading(false);
    }
  }, [asUser]);

  useEffect(() => {
    void fetchContacts();
  }, [fetchContacts]);

  const refetchContacts = useCallback(async (): Promise<void> => {
    try {
      const latest = await UlcmContactService.listContacts(asUser);
      setContacts(latest);
    } catch {
      // Non-fatal — WS event will reconcile.
    }
  }, [asUser]);

  // WS-driven state updates — no fetching, update local state directly from payload
  useEffect(() => {
    const handleWsEvent = (event: CustomEvent): void => {
      if (!event.detail || typeof event.detail.type !== 'string') {
        return;
      }
      const { type, data } = event.detail as { type: string; data: unknown };

      if (!isAccountHubEventForUser(data, targetUserIdRef.current)) {
        return;
      }

      if (type === 'contact_info_updated' && isFullContactSnapshot(data)) {
        // Full snapshot: replace contacts state directly
        setContacts(
          (prev) =>
            ({
              ...(prev ?? {
                pending_proposals: [],
                communication_preference: undefined,
                language_preference: undefined,
              }),
              ...(data as Partial<UlcmContactsPayload>),
            } as UlcmContactsPayload)
        );
      } else if (
        type === 'contact_info_pending_change' &&
        data &&
        typeof data === 'object'
      ) {
        const proposal = data as {
          otp_id: string;
          contact_type?: string;
          contact_value?: string;
        };
        setContacts((prev) => {
          if (!prev) {
            return prev;
          }
          const existing = prev.pending_proposals.some(
            (p) => p.otp_id === proposal.otp_id
          );
          if (existing) {
            return prev;
          }
          return {
            ...prev,
            pending_proposals: [
              ...prev.pending_proposals,
              {
                otp_id: proposal.otp_id,
                contact_type: (proposal.contact_type ?? 'email') as
                  | 'email'
                  | 'phone',
                contact_value: proposal.contact_value ?? '',
              },
            ],
          };
        });
      } else if (
        type === 'contact_info_pending_cancelled' &&
        data &&
        typeof data === 'object'
      ) {
        const { change_id } = data as { change_id?: string };
        const cancelledId = change_id;
        if (cancelledId) {
          setContacts((prev) => {
            if (!prev) {
              return prev;
            }
            return {
              ...prev,
              pending_proposals: prev.pending_proposals.filter(
                (p) => p.otp_id !== cancelledId
              ),
            };
          });
        }
      }
    };

    document.addEventListener(
      'UserWebSocketEvent',
      handleWsEvent as EventListener
    );
    return () => {
      document.removeEventListener(
        'UserWebSocketEvent',
        handleWsEvent as EventListener
      );
    };
  }, []);

  // ── Accept / Reject pending proposals ────────────────────────────────────
  const handleReject = useCallback(
    async (otpId: string): Promise<void> => {
      try {
        await UlcmContactService.cancelOwnProposal(otpId, undefined, asUser);
      } catch {
        // If the OTP no longer exists (404), treat as already cancelled
      }
    },
    [asUser]
  );

  const handleConfirmOtp = useCallback(
    async (otpId: string): Promise<void> => {
      if (confirmCode.length < 6) {
        return;
      }
      setConfirmSaving(true);
      setConfirmError(null);
      const proposal = contacts?.pending_proposals?.find(
        (p) => p.otp_id === otpId
      );
      const oldPrimaryEmailId =
        contacts?.emails?.find((e) => e.is_primary)?.id ?? null;
      const oldPrimaryPhoneId =
        contacts?.phones?.find((p) => p.is_primary)?.id ?? null;
      try {
        const result = await UlcmContactService.confirmOtp(
          'contact_change',
          confirmCode,
          asUser
        );
        if (result.confirmed) {
          setConfirmingOtpId(null);
          setConfirmCode('');
          // Refetch to pick up the newly-verified (and auto-promoted) row.
          // The WS event will catch up if this fails.
          try {
            const latest = await UlcmContactService.listContacts(asUser);
            setContacts(latest);
            if (proposal?.contact_type === 'email') {
              const promoted = latest.emails.find(
                (e) => e.email === proposal.contact_value && e.is_primary
              );
              if (promoted) {
                emailSectionRef.current?.promotePrimary(
                  promoted.id,
                  oldPrimaryEmailId
                );
              }
            } else if (proposal?.contact_type === 'phone') {
              const digits = (value: string): string =>
                value.replace(/\D/g, '');
              const promoted = latest.phones.find(
                (p) =>
                  digits(p.phone_number) === digits(proposal.contact_value) &&
                  p.is_primary
              );
              if (promoted) {
                phoneSectionRef.current?.promotePrimary(
                  promoted.id,
                  oldPrimaryPhoneId
                );
              }
            }
          } catch {
            // Non-fatal — WS event will reconcile.
          }
        } else if (result.is_locked) {
          setConfirmError(
            'Too many failed attempts. This proposal has been locked.'
          );
        } else {
          setConfirmError(
            `Invalid code. ${result.attempts_remaining} attempt(s) remaining.`
          );
        }
      } catch {
        setConfirmError('Verification failed. Please try again.');
      } finally {
        setConfirmSaving(false);
      }
    },
    [confirmCode, asUser, contacts]
  );

  // ── Contact section callbacks ─────────────────────────────────────────────
  const handleProposalCreated = useCallback(
    (proposal: PendingProposal): void => {
      setContacts((prev) => {
        if (!prev) {
          return prev;
        }
        if (prev.pending_proposals.some((p) => p.otp_id === proposal.otp_id)) {
          return prev;
        }
        return {
          ...prev,
          pending_proposals: [...prev.pending_proposals, proposal],
        };
      });
      setConfirmingOtpId(proposal.otp_id);
      setConfirmCode('');
      setConfirmError(null);
    },
    []
  );

  const requestRemove = useCallback((target: DeleteTarget): void => {
    setDeleteTarget(target);
  }, []);

  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!deleteTarget) {
      return;
    }
    setDeleteSaving(true);
    setDeleteError(null);
    try {
      if (deleteTarget.type === 'email') {
        await UlcmContactService.deleteEmail(deleteTarget.id, asUser);
      } else {
        await UlcmContactService.deletePhone(deleteTarget.id, asUser);
      }
      setDeleteTarget(null);
      // Refetch so backend-promoted primary is reflected correctly
      try {
        const latest = await UlcmContactService.listContacts(asUser);
        setContacts(latest);
      } catch {
        // Non-fatal — WS event will reconcile
      }
    } catch (err: unknown) {
      setDeleteError(
        extractApiError(err, 'Could not remove contact. Please try again.')
      );
    } finally {
      setDeleteSaving(false);
    }
  }, [deleteTarget, asUser]);

  const pendingProposals = contacts?.pending_proposals ?? [];
  const trimmedOwnerName = ownerName?.trim() || undefined;

  const emailRows: ContactRowData[] = (contacts?.emails ?? []).map((e) => ({
    id: e.id,
    value: e.email,
    isPrimary: e.is_primary,
    verified: e.verified,
  }));
  const phoneRows: ContactRowData[] = (contacts?.phones ?? []).map((p) => ({
    id: p.id,
    value: p.phone_number,
    isPrimary: p.is_primary,
    verified: p.verified,
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Stack gap={2.5}>
        {/* Pending Confirmation banner */}
        {pendingProposals.length > 0 && (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
              Pending Confirmation
            </Typography>
            {pendingProposals.map((p) => (
              <Stack key={p.otp_id} gap={1} sx={{ mb: 1.5 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Typography
                    sx={{ fontSize: 13, color: 'text.secondary' }}
                    noWrap
                  >
                    {p.contact_type === 'phone'
                      ? formatPhoneDisplay(p.contact_value)
                      : p.contact_value}
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        fontSize: 12,
                        borderRadius: '8px',
                        backgroundColor: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.dark' },
                      }}
                      onClick={() => {
                        setConfirmingOtpId(p.otp_id);
                        setConfirmCode('');
                        setConfirmError(null);
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: 12,
                        borderRadius: '8px',
                        borderColor: 'error.main',
                        color: 'error.main',
                      }}
                      onClick={() => void handleReject(p.otp_id)}
                    >
                      Reject
                    </Button>
                  </Stack>
                </Stack>

                {/* Inline OTP entry — OTP only, no TOTP (per product spec) */}
                {confirmingOtpId === p.otp_id && (
                  <Stack gap={1.5} sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Enter the verification code sent to confirm ownership.
                    </Typography>
                    {confirmError && (
                      <Alert severity="error" sx={{ py: 0 }}>
                        {confirmError}
                      </Alert>
                    )}
                    <OtpCodeInput
                      value={confirmCode}
                      onChange={setConfirmCode}
                      disabled={confirmSaving}
                      error={Boolean(confirmError)}
                    />
                    <PendingProposalResend proposal={p} asUser={asUser} />
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        onClick={() => {
                          setConfirmingOtpId(null);
                          setConfirmCode('');
                          setConfirmError(null);
                        }}
                        disabled={confirmSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={confirmCode.length < 6 || confirmSaving}
                        onClick={() => void handleConfirmOtp(p.otp_id)}
                        startIcon={
                          confirmSaving ? <CircularProgress size={14} /> : null
                        }
                      >
                        <span>Verify</span>
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            ))}
          </Box>
        )}

        {/* ── Email section ── */}
        {trimmedOwnerName && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: 13 }}>
              {proxyLoginWarning(trimmedOwnerName)}
            </Typography>
          </Alert>
        )}

        <ContactSection
          ref={emailSectionRef}
          type="email"
          rows={emailRows}
          asUser={asUser}
          ownerName={trimmedOwnerName}
          crossTypeCount={phoneRows.length}
          refetch={refetchContacts}
          onProposalCreated={handleProposalCreated}
          onRemoveRequest={requestRemove}
        />

        {/* ── Phone section ── */}
        <ContactSection
          ref={phoneSectionRef}
          type="phone"
          rows={phoneRows}
          asUser={asUser}
          ownerName={trimmedOwnerName}
          crossTypeCount={emailRows.length}
          refetch={refetchContacts}
          onProposalCreated={handleProposalCreated}
          onRemoveRequest={requestRemove}
        />
      </Stack>

      {/* ── Remove Contact Confirmation ── */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleteSaving) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove contact?</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            {deleteError && (
              <Alert severity="error" sx={{ py: 0 }}>
                {deleteError}
              </Alert>
            )}
            <Typography sx={{ fontSize: 14 }}>
              Remove{' '}
              <strong>
                {deleteTarget?.type === 'phone'
                  ? formatPhoneDisplay(deleteTarget.value)
                  : deleteTarget?.value}
              </strong>{' '}
              from{' '}
              {trimmedOwnerName
                ? `${trimmedOwnerName}'s account`
                : 'your account'}
              ?
            </Typography>
            {deleteTarget?.type === 'phone' && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Removing this phone number will archive any existing SMS
                conversations. Message history will be preserved.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteTarget(null);
              setDeleteError(null);
            }}
            disabled={deleteSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDeleteConfirm()}
            disabled={deleteSaving}
            startIcon={deleteSaving ? <CircularProgress size={14} /> : null}
          >
            <span>{deleteSaving ? 'Removing' : 'Remove'}</span>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountContactsEditor;
